import { Box, Button, Divider, Flex, Input, InputGroup, InputLeftAddon } from "@chakra-ui/react";
import LoanTypeFields from "components/loans/LoanTypeFields";
import BoxHeader from "components/elements/BoxHeader";
import { AlertsContext } from "providers/alerts";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FieldValue, FieldValueMap } from "utils/types";
import { mapLoanValues } from "../../FieldsTable";
import PropertyTypeFields from "components/loans/PropertyTypeFields";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getErrorMessage } from "utils/errorHandler";
import { SubmitApplicationButton } from "../SubmitApplicationButton";
import { LoanFieldModel } from "providers/loans/loan-field.model";
import { PropertyDetailFieldModel, PropertyValueItemModel } from "providers/loans/property.model";
import { LoanStatusModel } from "providers/loans/loan-status.model";
import { useLoanSection } from "hooks/useSections";
import { SectionModel } from "providers/loans/loan-section.model";
import moment from "moment";
import { useLoanApplicationPreferences } from "hooks/useLoanApplicationPreferences";
import { LoanSignature } from "@common/loans/signatures/loan-signature.model";
import { useLoanActions } from "providers/loans/mutations/loan-actions";

interface Field {
  identifier_key: string;
  id: string;
}

interface SectionWithLoanField extends SectionModel {
  fields: LoanFieldModel[];
}

const AmplendSectionFieldsEdit = ({
  onSave,
  columns = 2,
  onCancel,
  onChange,
  signatures,
}: {
  onSave: () => void;
  columns?: number;
  onCancel?: () => void;
  onChange?: (values: FieldValueMap) => void;
  signatures?: LoanSignature[];
}) => {
  const { user } = useTenant();
  const { loan } = useContext(LoanContext);
  const { showSuccess, showError } = useContext(AlertsContext);
  const { data: loanSections } = useLoanSection();
  const [isLoading, setIsLoading] = useState(false);

  const [fields, setFields] = useState<LoanFieldModel[]>([]);
  const [values, setValues] = useState<any>({});
  const [sections, setSections] = useState<SectionWithLoanField[]>([]);
  const [sectionsOnView, setSectionsOnView] = useState<SectionWithLoanField[]>([]);

  const [propertyFields, setPropertyFields] = useState<PropertyDetailFieldModel[]>([]);
  const [propertyValues, setPropertyValues] = useState<PropertyValueItemModel[]>([]);
  const [borrowerFields, setBorrowerFields] = useState<any[]>([]);
  const [borrowerValues, setBorrowerValues] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState<FieldValueMap>({});
  const fieldValuesRef = useRef<FieldValueMap>(fieldValues);
  const [missingFieldIds, setMissingFieldIds] = useState<string[]>([]);
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);

  const { data: preferences } = useLoanApplicationPreferences();

  const { applyChanges } = useLoanActions();

  const setFieldValue = useCallback(
    (field_id: string, value: FieldValue) => {
      fieldValuesRef.current = {
        ...fieldValuesRef.current,
        [field_id]: value,
      };
      onChange?.(fieldValuesRef.current);
      setFieldValues(fieldValuesRef.current);
      setEditedFields(prev => new Set(prev).add(field_id));
    },
    [onChange]
  );

  const getNumericPropertyValue = useCallback(
    (fieldId: number, defaultValue: number = 0) => {
      const propertyValue = propertyValues.find(field => field.property_field === fieldId)?.value
        ?.field;
      return Number(propertyValue || defaultValue);
    },
    [propertyValues]
  );

  const findFieldIdByIdentifierKey = useCallback(
    (identifierKey: string): string => {
      return fields.find(field => field.identifier_key === identifierKey)?.id ?? "";
    },
    [fields]
  );

  const calculateTLA = useCallback(
    (value: any) => {
      // Get the underlying loan field ids used in the calculations
      const credit_report_date_1_id = findFieldIdByIdentifierKey("credit_report_date_1");

      const credit_report_date_2_id = findFieldIdByIdentifierKey("credit_report_date_2");

      const credit_report_date_3_id = findFieldIdByIdentifierKey("credit_report_date_3");

      const origination_date_id = findFieldIdByIdentifierKey("origination_date");

      const loan_term_months_id = findFieldIdByIdentifierKey("loan_term_months");

      const initial_loan_amount_id = findFieldIdByIdentifierKey("initial_loan_amount");

      const financed_budget_amount_id = findFieldIdByIdentifierKey("financed_budget_amount");

      const interest_rate_id = findFieldIdByIdentifierKey("interest_rate");

      const monthly_effective_rent_id = findFieldIdByIdentifierKey("monthly_effective_rent");

      const monthly_payment_id = findFieldIdByIdentifierKey("monthly_payment");

      const as_is_appraisal_value_id = findFieldIdByIdentifierKey("as_is_appraisal_value");

      const cost_basis_id = findFieldIdByIdentifierKey("cost_basis");

      const total_loan_amount_id = findFieldIdByIdentifierKey("total_loan_amount");

      const after_repair_appraisal_value_id = findFieldIdByIdentifierKey(
        "after_repair_appraisal_value"
      );

      let loan_purchase_price_id = findFieldIdByIdentifierKey("loan_purchase_price");

      let monthly_effective_rents = propertyFields.filter((val: any) =>
        val.data_key?.startsWith("monthly_effective_rent_")
      );

      const total_origination_points_id = findFieldIdByIdentifierKey("total_origination_points");

      const target_closing_date_id = findFieldIdByIdentifierKey("target_closing_date");

      const appraisal_date_id = findFieldIdByIdentifierKey("appraisal_date");

      const unit_occupied_id = findFieldIdByIdentifierKey("unit_occupied");

      const number_of_units_id = findFieldIdByIdentifierKey("number_of_units");

      const monthly_market_rent_id = findFieldIdByIdentifierKey("monthly_market_rent");

      const monthly_in_place_lease_rent_id = "monthly_in_place_lease_rent";

      const total_rehab_budget_id = findFieldIdByIdentifierKey("total_rehab_budget");

      const approved_draw_1_id = findFieldIdByIdentifierKey("approved_draw_1");
      const approved_draw_2_id = findFieldIdByIdentifierKey("approved_draw_2");
      const approved_draw_3_id = findFieldIdByIdentifierKey("approved_draw_3");
      const approved_draw_4_id = findFieldIdByIdentifierKey("approved_draw_4");
      const approved_draw_5_id = findFieldIdByIdentifierKey("approved_draw_5");
      const approved_draw_6_id = findFieldIdByIdentifierKey("approved_draw_6");
      const approved_draw_7_id = findFieldIdByIdentifierKey("approved_draw_7");
      const approved_draw_8_id = findFieldIdByIdentifierKey("approved_draw_8");

      const background_credit_report_id = findFieldIdByIdentifierKey("background_credit_report");

      const legal_fee_id = findFieldIdByIdentifierKey("legal_fee");

      const underwriting_processing_fee_id = findFieldIdByIdentifierKey(
        "underwriting_processing_fee"
      );

      const title_review_handling_fee_id = findFieldIdByIdentifierKey("title_review_handling_fee");

      const flood_certification_id = findFieldIdByIdentifierKey("flood_certification");

      const overnight_mail_fee_id = findFieldIdByIdentifierKey("overnight_mail_fee");

      const wire_fee_id = findFieldIdByIdentifierKey("wire_fee");

      const feasibility_study_id = findFieldIdByIdentifierKey("feasibility_study");

      const valuation_review_id = findFieldIdByIdentifierKey("valuation_review");

      const estimated_title_fees_id = findFieldIdByIdentifierKey("estimated_title_fees");

      const estimated_insurance_cost_id = findFieldIdByIdentifierKey("estimated_insurance_cost");

      const estimated_appraisal_cost_id = findFieldIdByIdentifierKey("estimated_appraisal_cost");

      const annual_insurance_premium_id = findFieldIdByIdentifierKey("annual_insurance_premium");
      const annual_property_taxes_id = findFieldIdByIdentifierKey("annual_property_taxes");
      const annual_hoa_id = findFieldIdByIdentifierKey("annual_hoa");
      const interest_only_period_months_id = findFieldIdByIdentifierKey(
        "interest_only_period_months"
      );
      const loan_purchase_price_percent_id = findFieldIdByIdentifierKey(
        "loan_purchase_price_percent"
      );

      // Get the destination loan field ids for the calculations
      const appraisal_expiration_date_id = findFieldIdByIdentifierKey("appraisal_expiration_date");
      const approved_draw_cumulative_id = findFieldIdByIdentifierKey("approved_draw_cumulative");
      const as_is_ltv_id = findFieldIdByIdentifierKey("as_is_ltv");
      const credit_expiration_date_1_id = findFieldIdByIdentifierKey("credit_expiration_date_1");
      const credit_expiration_date_2_id = findFieldIdByIdentifierKey("credit_expiration_date_2");
      const credit_expiration_date_3_id = findFieldIdByIdentifierKey("credit_expiration_date_3");
      const dscr_id = findFieldIdByIdentifierKey("dscr");
      const down_payment_id = findFieldIdByIdentifierKey("down_payment");
      const estimated_cash_due_at_closing_id = findFieldIdByIdentifierKey(
        "estimated_cash_due_at_closing"
      );
      const first_payment_date_id = findFieldIdByIdentifierKey("first_payment_date");

      const ltarv_id = findFieldIdByIdentifierKey("ltarv");
      const ltc_id = findFieldIdByIdentifierKey("ltc");
      const maturity_date_id = findFieldIdByIdentifierKey("maturity_date");
      const origination_fee_id = findFieldIdByIdentifierKey("origination_fee");
      const prepaid_daily_interest_charge_id = findFieldIdByIdentifierKey(
        "prepaid_daily_interest_charge"
      );
      const project_profitability_id = findFieldIdByIdentifierKey("project_profitability");
      const remaining_budget_amount_id = findFieldIdByIdentifierKey("remaining_budget_amount");
      const total_monthly_effective_rent_id = findFieldIdByIdentifierKey(
        "total_monthly_effective_rent"
      );
      const purchase_price_id = findFieldIdByIdentifierKey("purchase_price");

      // Helper function to safely get numeric values
      const getNumericValueInLoanFields = (fieldId: string, defaultValue: number = 0) => {
        return Number(value[fieldId] || defaultValue);
      };

      const getNumericValueInPropertyFields = (fieldId: number, defaultValue: number = 0) => {
        const propertyValue = propertyValues.find(value => value.property_field === fieldId);

        return Number(propertyValue?.value?.field || defaultValue);
      };

      // Helper function to safely perform division
      const safeDivide = (numerator: number, denominator: number) => {
        return denominator !== 0 ? numerator / denominator : 0;
      };

      if (value[credit_report_date_1_id] && credit_expiration_date_1_id) {
        const dateValue = moment(value[credit_report_date_1_id], "YYYY-MM-DD");
        dateValue.add(3, "month");
        value[credit_expiration_date_1_id] = dateValue.format("YYYY-MM-DD");
      } else {
        value[credit_expiration_date_1_id] = null;
      }

      if (value[credit_report_date_2_id] && credit_expiration_date_2_id) {
        const dateValue = moment(value[credit_report_date_2_id], "YYYY-MM-DD");
        dateValue.add(3, "month");
        value[credit_expiration_date_2_id] = dateValue.format("YYYY-MM-DD");
      } else {
        value[credit_expiration_date_2_id] = null;
      }

      if (value[credit_report_date_3_id] && credit_expiration_date_3_id) {
        const dateValue = moment(value[credit_report_date_3_id], "YYYY-MM-DD");
        dateValue.add(3, "month");
        value[credit_expiration_date_3_id] = dateValue.format("YYYY-MM-DD");
      } else {
        value[credit_expiration_date_3_id] = null;
      }

      if (value[origination_date_id] && first_payment_date_id) {
        const originationDate = moment(value[origination_date_id], "YYYY-MM-DD");

        const firstPaymentDate = originationDate.add(2, "month").startOf("month");

        value[first_payment_date_id] = firstPaymentDate.format("YYYY-MM-DD");
      } else {
        value[first_payment_date_id] = null;
      }

      if (value[origination_date_id] && maturity_date_id) {
        const firstPaymentDate = moment(value[origination_date_id], "YYYY-MM-DD");

        const loanTermMonths = parseInt(value[loan_term_months_id]) || 0;

        const maturityDate = firstPaymentDate.add(loanTermMonths, "month");

        value[maturity_date_id] = maturityDate.format("YYYY-MM-DD");
      } else {
        value[maturity_date_id] = null;
      }

      if (total_loan_amount_id) {
        value[total_loan_amount_id] = (
          Number(value[initial_loan_amount_id] || 0) +
          Number(value[financed_budget_amount_id] || 0) +
          Number(value[total_rehab_budget_id] || 0)
        ).toFixed(2);
      }

      if (monthly_payment_id && value[initial_loan_amount_id] && value[interest_rate_id]) {
        const interestRate = getNumericValueInLoanFields(interest_rate_id) / 100;
        const monthlyInterestRate = interestRate / 12;

        // Short-term
        if (loan?.loan_type?.description === "Short-Term") {
          value[monthly_payment_id] = (
            (Number(value[initial_loan_amount_id] || 0) * interestRate) /
            12
          ).toFixed(2);
        } else if (loan?.loan_type?.description === "Long-Term") {
          // Monthly PITIA (i.e. Principal, Interest, Tax, Insurance, and Association Fee)
          const loanAmount = Number(value[initial_loan_amount_id] || 0);
          const interestOnlyPeriodMonths = Number(value[interest_only_period_months_id] || 0);

          let principalAndInterest: number;

          if (interestOnlyPeriodMonths > 0) {
            // Calculate interest-only payment
            const interestOnlyPayment = loanAmount * monthlyInterestRate;

            // The payment during the interest-only period
            principalAndInterest = interestOnlyPayment;
          } else {
            // Calculate the fully amortized payment
            principalAndInterest =
              (loanAmount * monthlyInterestRate) /
              (1 - Math.pow(1 + monthlyInterestRate, -interestOnlyPeriodMonths));
          }

          const monthlyInsurance = safeDivide(Number(value[annual_insurance_premium_id] || 0), 12);
          const monthlyTax = safeDivide(Number(value[annual_property_taxes_id] || 0), 12);
          const monthlyHOA = safeDivide(Number(value[annual_hoa_id] || 0), 12);

          value[monthly_payment_id] = (
            Number(value[principalAndInterest] || 0) +
            Number(monthlyInsurance || 0) +
            Number(monthlyTax || 0) +
            Number(monthlyHOA || 0)
          ).toFixed(2);
        }
      }

      if (monthly_effective_rent_id && total_monthly_effective_rent_id) {
        const monthlyEffectiveRent = getNumericValueInLoanFields(initial_loan_amount_id);
        value[total_monthly_effective_rent_id] = monthlyEffectiveRent.toFixed(2);

        if (monthly_payment_id && dscr_id) {
          const monthlyPayment = getNumericValueInLoanFields(monthly_payment_id);
          value[dscr_id] = monthlyPayment
            ? safeDivide(monthlyEffectiveRent, monthlyPayment).toFixed(2)
            : null;
        }
      }

      if (total_monthly_effective_rent_id && monthly_effective_rents?.length > 0) {
        let monthlyEffectiveRent = 0;
        for (let i = 0; i < monthly_effective_rents.length; i++) {
          monthlyEffectiveRent += getNumericPropertyValue(monthly_effective_rents[i].id);
        }
        value[total_monthly_effective_rent_id] = monthlyEffectiveRent.toFixed(2);
      }

      if (initial_loan_amount_id && as_is_appraisal_value_id && as_is_ltv_id) {
        const initialLoanAmount = getNumericValueInLoanFields(initial_loan_amount_id);
        const asIsAppraisalValue = getNumericValueInLoanFields(as_is_appraisal_value_id);
        value[as_is_ltv_id] = asIsAppraisalValue
          ? safeDivide(initialLoanAmount, asIsAppraisalValue).toFixed(2)
          : null;
      }

      if (initial_loan_amount_id && cost_basis_id && ltc_id) {
        const initialLoanAmount = getNumericValueInLoanFields(initial_loan_amount_id);
        const costBasis = getNumericValueInLoanFields(cost_basis_id);
        const ltc = getNumericValueInLoanFields(ltc_id);
        value[ltc_id] = costBasis ? safeDivide(initialLoanAmount, costBasis).toFixed(2) : null;
      }

      if (total_loan_amount_id && after_repair_appraisal_value_id && ltarv_id) {
        const totalLoanAmount = getNumericValueInLoanFields(total_loan_amount_id);
        const afterRepairAppraisalValue = getNumericValueInLoanFields(
          after_repair_appraisal_value_id
        );
        value[ltarv_id] = afterRepairAppraisalValue
          ? safeDivide(totalLoanAmount, afterRepairAppraisalValue).toFixed(2)
          : null;
      }

      if (purchase_price_id && initial_loan_amount_id && down_payment_id) {
        const purchasePrice = getNumericValueInLoanFields(purchase_price_id);

        const initialLoanAmount = getNumericValueInLoanFields(initial_loan_amount_id);

        const downPaymentValue = Math.max(purchasePrice - initialLoanAmount, 0).toFixed(2);

        value[down_payment_id] = downPaymentValue;
      }

      if (loan_purchase_price_id && initial_loan_amount_id && loan_purchase_price_percent_id) {
        const loanPurchasePricePercent =
          getNumericValueInLoanFields(loan_purchase_price_percent_id) / 100;

        const initialLoanAmount = getNumericValueInLoanFields(initial_loan_amount_id);

        value[loan_purchase_price_id] = (loanPurchasePricePercent * initialLoanAmount).toFixed(2);
      }

      if (total_loan_amount_id && total_origination_points_id && origination_fee_id) {
        const totalLoanAmount = getNumericValueInLoanFields(total_loan_amount_id);
        const totalOriginationPoints = getNumericValueInLoanFields(total_origination_points_id);
        value[origination_fee_id] = (totalLoanAmount * (totalOriginationPoints / 100)).toFixed(2);
      }

      if (
        target_closing_date_id &&
        initial_loan_amount_id &&
        interest_rate_id &&
        prepaid_daily_interest_charge_id
      ) {
        const targetClosingDate = value[target_closing_date_id]
          ? moment(value[target_closing_date_id], "YYYY-MM-DD")
          : null;

        const initialLoanAmount = getNumericValueInLoanFields(initial_loan_amount_id);

        const interestRate = getNumericValueInLoanFields(interest_rate_id) / 100;

        if (targetClosingDate) {
          const daysInMonth = targetClosingDate.daysInMonth() + 1;

          const currentDay = targetClosingDate.date();
          const daysToEndOfMonth = daysInMonth - currentDay;

          const prepaidDailyInterestChargeValue = (
            daysToEndOfMonth *
            ((initialLoanAmount * interestRate) / 360)
          ).toFixed(2);

          value[prepaid_daily_interest_charge_id] = prepaidDailyInterestChargeValue;
        } else {
          value[prepaid_daily_interest_charge_id] = null;
        }
      }

      if (estimated_cash_due_at_closing_id) {
        value[estimated_cash_due_at_closing_id] = (
          Number(value[down_payment_id] || 0) +
          Number(value[origination_fee_id] || 0) +
          Number(value[background_credit_report_id] || 0) +
          Number(value[legal_fee_id] || 0) +
          Number(value[underwriting_processing_fee_id] || 0) +
          Number(value[title_review_handling_fee_id] || 0) +
          Number(value[flood_certification_id] || 0) +
          Number(value[overnight_mail_fee_id] || 0) +
          Number(value[wire_fee_id] || 0) +
          Number(value[feasibility_study_id] || 0) +
          Number(value[valuation_review_id] || 0) +
          Number(value[estimated_title_fees_id] || 0) +
          Number(value[financed_budget_amount_id] || 0) +
          Number(value[estimated_insurance_cost_id] || 0) +
          Number(value[estimated_appraisal_cost_id] || 0) +
          Number(value[prepaid_daily_interest_charge_id] || 0)
        ).toFixed(2);
      }

      if (appraisal_date_id && appraisal_expiration_date_id) {
        const appraisalDate = value[appraisal_date_id] ? new Date(value[appraisal_date_id]) : null;
        if (appraisalDate) {
          const expirationDate = new Date(appraisalDate);
          expirationDate.setDate(expirationDate.getDate() + 90);
          value[appraisal_expiration_date_id] = expirationDate.toISOString().split("T")[0];
        } else {
          value[appraisal_expiration_date_id] = null;
        }
      }

      if (unit_occupied_id && number_of_units_id) {
        const unitOccupied = getNumericValueInLoanFields(unit_occupied_id);
        const numberOfUnits = getNumericValueInLoanFields(number_of_units_id);
        if (numberOfUnits === 1) {
          value[unit_occupied_id] = unitOccupied >= 1 ? "Y" : "N";
        } else if (numberOfUnits === 2) {
          value[unit_occupied_id] = unitOccupied >= 2 ? "Y" : "N";
        } else if (numberOfUnits === 3) {
          value[unit_occupied_id] = unitOccupied >= 2 ? "Y" : "N";
        } else if (numberOfUnits === 4) {
          value[unit_occupied_id] = unitOccupied >= 3 ? "Y" : "N";
        } else {
          value[unit_occupied_id] = null;
        }
      }

      if (
        monthly_market_rent_id &&
        monthly_in_place_lease_rent_id &&
        unit_occupied_id &&
        monthly_effective_rent_id
      ) {
        const monthlyMarketRent = getNumericValueInLoanFields(monthly_market_rent_id);
        const monthlyInPlaceLeaseRent = getNumericValueInLoanFields(monthly_in_place_lease_rent_id);
        const unitOccupied = value[unit_occupied_id] === "Y";
        value[monthly_effective_rent_id] = unitOccupied
          ? Math.min(monthlyMarketRent * 1.05, monthlyInPlaceLeaseRent).toFixed(2)
          : monthlyMarketRent.toFixed(2);
      }

      if (purchase_price_id && cost_basis_id) {
        const purchasePrice = getNumericValueInLoanFields(purchase_price_id);

        // Calculate eligible closing costs based on the formula
        const eligibleClosingCosts =
          getNumericValueInLoanFields(background_credit_report_id) +
          getNumericValueInLoanFields(underwriting_processing_fee_id) +
          getNumericValueInLoanFields(flood_certification_id) +
          getNumericValueInLoanFields(wire_fee_id) +
          getNumericValueInLoanFields(valuation_review_id) +
          getNumericValueInLoanFields(estimated_insurance_cost_id) +
          getNumericValueInLoanFields(prepaid_daily_interest_charge_id) +
          getNumericValueInLoanFields(legal_fee_id) +
          getNumericValueInLoanFields(title_review_handling_fee_id) +
          getNumericValueInLoanFields(overnight_mail_fee_id) +
          getNumericValueInLoanFields(feasibility_study_id) +
          getNumericValueInLoanFields(estimated_title_fees_id) +
          getNumericValueInLoanFields(estimated_appraisal_cost_id);

        value[cost_basis_id] = (purchasePrice + eligibleClosingCosts).toFixed(2);
      }

      if (
        after_repair_appraisal_value_id &&
        cost_basis_id &&
        total_rehab_budget_id &&
        project_profitability_id
      ) {
        const afterRepairAppraisalValue = getNumericValueInLoanFields(
          after_repair_appraisal_value_id
        );
        const costBasis = getNumericValueInLoanFields(cost_basis_id);
        const totalRehabBudget = getNumericValueInLoanFields(total_rehab_budget_id);
        if (afterRepairAppraisalValue === 0) {
          value[project_profitability_id] = null;
        } else {
          const profitability = safeDivide(
            afterRepairAppraisalValue - costBasis - totalRehabBudget,
            costBasis + totalRehabBudget
          );
          value[project_profitability_id] = (profitability * 100).toFixed(2) + "%";
        }
      }

      if (approved_draw_cumulative_id) {
        const approvedDraw1 = getNumericValueInLoanFields(approved_draw_1_id);
        const approvedDraw2 = getNumericValueInLoanFields(approved_draw_2_id);
        const approvedDraw3 = getNumericValueInLoanFields(approved_draw_3_id);
        const approvedDraw4 = getNumericValueInLoanFields(approved_draw_4_id);
        const approvedDraw5 = getNumericValueInLoanFields(approved_draw_5_id);
        const approvedDraw6 = getNumericValueInLoanFields(approved_draw_6_id);
        const approvedDraw7 = getNumericValueInLoanFields(approved_draw_7_id);
        const approvedDraw8 = getNumericValueInLoanFields(approved_draw_8_id);
        value[approved_draw_cumulative_id] = (
          approvedDraw1 +
          approvedDraw2 +
          approvedDraw3 +
          approvedDraw4 +
          approvedDraw5 +
          approvedDraw6 +
          approvedDraw7 +
          approvedDraw8
        ).toFixed(2);
      }

      if (remaining_budget_amount_id) {
        const financedBudgetAmount = getNumericValueInLoanFields(financed_budget_amount_id);
        const approvedDrawCumulative = getNumericValueInLoanFields(approved_draw_cumulative_id);
        value[remaining_budget_amount_id] = (financedBudgetAmount - approvedDrawCumulative).toFixed(
          2
        );
      }
    },
    [
      findFieldIdByIdentifierKey,
      propertyFields,
      propertyValues,
      loan?.loan_type?.description,
      getNumericPropertyValue,
    ]
  );

  async function onSubmit(
    showSuccessMessage: boolean = true,
    submitApplication: boolean = false
  ): Promise<boolean> {
    try {
      // Filter out the Address/Location field from required validation
      if (!loan?.status.isDraft || submitApplication) {
        const missingFields: { id: string; label: string }[] =
          fields
            .filter(
              field =>
                field.required && !fieldValues[field.id] && field.label !== "Address or Location"
            )
            .map(field => ({
              id: field.id,
              label: field.label,
            })) || [];

        if (preferences?.enable_signatures) {
          const enableCoBorrowerField = fields.filter(
            field => field.identifier_key === "enable_co_borrower"
          );

          let requiredSignaturesCount = 0;

          if (enableCoBorrowerField && enableCoBorrowerField.length > 0) {
            const enableCoBorrowerValue = fieldValues[enableCoBorrowerField[0].id];

            if (enableCoBorrowerValue?.toLowerCase() === "yes") {
              requiredSignaturesCount = 2;
            } else {
              requiredSignaturesCount = 1;
            }
          }

          if (requiredSignaturesCount > (signatures?.length ?? 0)) {
            missingFields.push({
              id: "Signature",
              label: requiredSignaturesCount === 1 ? "Signature" : "Signatures",
            });
          }
        }

        if (missingFields.length > 0) {
          setMissingFieldIds(missingFields.map((field: any) => field.id));

          throw new Error(`Required fields missing: ${missingFields.map(f => f.label).join(", ")}`);
        }
      }

      setIsLoading(true);

      const field_values = fields
        .filter((field: any) => editedFields.has(field.id))
        .map((field: any) => ({
          ...field,
          value: fieldValues[field?.id],
          loan_value_id: loan?.values.find((val: any) => val.loan_field.id === field.id)?.id,
        }));

      // data for the loans table to get the amortization schedule
      const interestRateId = (
        (fields.find((val: any) => val.data_key === "custom_interest_rate") || {}) as Field
      ).id;
      const fundingDateId = (
        (fields.find((val: any) => val.data_key === "funding_date") || {}) as Field
      ).id;
      const durationId = ((fields.find((val: any) => val.data_key === "duration") || {}) as Field)
        .id;
      const holidayPeriodId = (
        (fields.find((val: any) => val.data_key === "holiday_period") || {}) as Field
      ).id;
      const getEffectiveDateId = (
        (fields.find((val: any) => val.data_key === "effective_date") || {}) as Field
      ).id;

      const interestRate = fieldValues[interestRateId]?.InterestRate;
      const interestLoanType = fieldValues[interestRateId]?.LoanType;
      const funding_date = fieldValues[fundingDateId];
      const getDurationValue = fieldValues[durationId];
      const getHolidayPeriodValue = fieldValues[holidayPeriodId];
      const getEffectiveDate = fieldValues[getEffectiveDateId];
      const companyId = loan?.lender_company?.id;

      const startDate = new Date(funding_date);
      const durationInMonths = parseInt(getDurationValue) || 0;
      const holidayPeriodInMonths = parseInt(getHolidayPeriodValue) || 0;

      const getEffectiveDateToValue = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + durationInMonths + holidayPeriodInMonths,
        startDate.getDate()
      );
      const customFundedValue = interestLoanType ? true : false;

      const updateLoan: any = {
        id: loan?.id,
        fields: field_values,
        values: values,
        interest_rate_: interestRate,
        interest_loan_type_: interestLoanType,
        funding_date_: funding_date,
        duration_: getDurationValue,
        holiday_period_: getHolidayPeriodValue,
        company_id: companyId,
        effective_date: getEffectiveDate,
        effective_date_to_date: getEffectiveDateToValue,
        funded_value_: customFundedValue,
        propertyValues: propertyValues,
        borrowerValues: borrowerValues,
      };

      if (submitApplication) {
        const statuses: LoanStatusModel[] =
          user.lender_company == null ? user.company.statuses : user.lender_company.statuses;

        //first ordered by order
        const submitted_status = statuses
          .sort((a: any, b: any) => a.order - b.order)
          .find((s: any) => s.isSubmitted);

        if (submitted_status) {
          updateLoan.status = submitted_status.id;
        }
      }

      await applyChanges.mutateAsync(updateLoan);

      if (showSuccessMessage) {
        showSuccess({
          title: "Loan Updated",
          description: "The loan details have been successfully saved.",
        });
      }

      onSave();

      return true;
    } catch (err) {
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });

      console.log(err);

      return false;
    } finally {
      setIsLoading(false);
    }
  }

  const filterApplicationFields = useCallback(
    (field: any) => {
      if (user.type === "borrower") {
        return field.show_on_borrower_application;
      } else if (user.type === "lender") {
        return field.show_on_lender_application;
      }

      return false;
    },
    [user.type]
  );

  const filterLoanFields = useCallback(
    (field: any) => {
      if (user.type === "borrower") {
        return field.show_on_borrower_loan;
      } else if (user.type === "lender") {
        return field.show_on_lender_loan;
      }

      return false;
    },
    [user.type]
  );

  const applyFilter = useCallback(() => {
    if (sections.length > 0) {
      // Filter fields within each section based on activeTab and filterText
      const filteredSections = sections
        .map(section => ({
          ...section,
          fields: section.fields.filter(
            field => !filterText || field.label.toLowerCase().includes(filterText.toLowerCase())
          ),
        }))
        // Remove empty sections
        .filter(section => section.fields.length > 0)
        .sort((a, b) => a.order - b.order);

      setSectionsOnView(filteredSections);
    }
  }, [filterText, sections]);

  const checkDifferentSection = useCallback(
    (section: any) => {
      if (
        section.section_key &&
        section.section_key === "property_details_rwa" &&
        (loan?.property?.values?.length ?? 0) > 0
      ) {
        return "property_details";
      }
      if (
        section.section_key &&
        section.section_key === "borrower_details_rwa" &&
        (loan?.borrower?.borrower_values?.length ?? 0) > 0
      ) {
        return "borrower_details";
      }
      return false;
    },
    [loan]
  );

  useEffect(() => {
    if (loan && loanSections) {
      setValues(mapLoanValues(loan.values));

      const fields = loan.fields.filter(field =>
        loan?.status.isSubmitted ? filterLoanFields(field) : filterApplicationFields(field)
      );

      setFields(fields);

      const sections: SectionWithLoanField[] = loanSections.map(section => ({
        ...section,
        fields: fields.filter(field => field.section?.id === section.id) ?? [],
      }));

      setSections(sections);

      if (loan?.property?.property_detail_fields && loan?.property?.values) {
        setPropertyFields(loan?.property?.property_detail_fields);
        setPropertyValues(loan?.property?.values);
      }

      if (loan?.borrower?.borrower_values && loan?.borrower?.company?.borrower_fields) {
        setBorrowerFields(loan?.borrower?.company?.borrower_fields);
        setBorrowerValues(loan?.borrower?.borrower_values);
      }
    }
  }, [filterApplicationFields, filterLoanFields, loan, loanSections]);

  useEffect(() => {
    if (fields.length > 0 && !isLoaded) {
      fields.forEach((field: any) => {
        setFieldValue(field.id, field.data_key ? (loan as any)[field.data_key] : values[field.id]);
      });

      setIsLoaded(true);
    }
  }, [fields, loan, setFieldValue, values, isLoaded]);

  useEffect(() => {
    calculateTLA(fieldValuesRef.current);
  }, [calculateTLA, fieldValues]);

  useEffect(() => {
    if (sections.length > 0) {
      applyFilter();
      setIsLoading(false);
    }
  }, [sections, filterText, applyFilter]);

  return (
    <Box>
      <Box>
        <Flex alignItems="center" gap="2" mb="20px">
          <InputGroup
            py="0px"
            height="35px"
            color="gray.300"
            fontSize="14px"
            fontWeight="500"
            borderWidth="1.5px"
            borderColor="#E0E0E0"
            borderRadius="7px"
          >
            <InputLeftAddon height="32px" pr="0px">
              <FontAwesomeIcon icon={["far", "magnifying-glass"]} fontSize="14px" />
            </InputLeftAddon>
            <Input
              fontSize="14px"
              fontWeight="500"
              pl="8px"
              py="0px"
              height="31px"
              lineHeight="22px"
              value={filterText}
              onChange={ev => {
                setFilterText(ev.target.value);
                applyFilter();
              }}
              placeholder="Search"
            />
          </InputGroup>

          <Box>
            <Button
              onClick={onCancel}
              variant="outline"
              height="35px"
              borderRadius="7px"
              fontSize="14px"
              fontWeight="500"
              color="gray.200"
              borderColor="gray.100"
              _hover={{
                color: "#2E2C32",
                borderColor: "#2E2C32",
              }}
            >
              Cancel
            </Button>
          </Box>

          <Box>
            <Button
              variant="outline"
              height="35px"
              borderRadius="7px"
              fontSize="14px"
              fontWeight="500"
              color="gray.200"
              borderColor="gray.100"
              _hover={{
                color: "#2E2C32",
                borderColor: "#2E2C32",
              }}
              onClick={() => onSubmit(true, false)}
              isLoading={isLoading}
            >
              {loan?.status.isDraft ? "Save As Draft" : "Save Changes"}
            </Button>
          </Box>

          {!loan?.status.isSubmitted && (
            <Box>
              <SubmitApplicationButton
                onLoading={setIsLoading}
                beforeSubmit={() => onSubmit(false, true)}
              />
            </Box>
          )}
        </Flex>
      </Box>

      {(() => {
        const filteredSections = sectionsOnView.filter(
          (section: any) =>
            (!section.underwriting && user.type === "borrower") || user.type === "lender"
        );

        // Sort sections based on the order property from the database
        const sortedSections = filteredSections.sort((a, b) => a.order - b.order);

        return sortedSections.map(section => (
          <Box mt="20px" key={section.id}>
            <BoxHeader mb="0px" title={section.section_name} />
            <Divider mt="10px" mb="20px" />
            {checkDifferentSection(section) === "property_details" ? (
              <PropertyTypeFields
                columns={columns}
                fields={propertyFields}
                values={propertyValues}
                setValues={setPropertyValues}
                name="property_details"
              />
            ) : checkDifferentSection(section) === "borrower_details" ? (
              <PropertyTypeFields
                columns={columns}
                fields={borrowerFields}
                values={borrowerValues}
                setValues={setBorrowerValues}
                name="borrower_details"
              />
            ) : (
              // Loan Fields
              <LoanTypeFields
                columns={columns}
                fields={section.fields}
                values={fieldValues}
                setValue={setFieldValue}
                missingFieldIds={missingFieldIds}
                calculateTLA={calculateTLA}
              />
            )}
          </Box>
        ));
      })()}
    </Box>
  );
};

export default AmplendSectionFieldsEdit;
